import { bootstrapSessionOutcome } from '@jq4race/api-client';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { setAuthBootOutcome } from '../../../app/auth-state.js';
import { resolveIdentity } from '../../auth/domain/resolve-identity.js';
import { foregroundDrainer } from '../application/foreground-drainer-instance.js';

/**
 * useOfflineShellRecovery — CRITICAL fix (adversarial review): reopening the
 * app OFFLINE and later reconnecting WITHOUT a manual page reload never
 * synced the outbox.
 *
 * Root cause: `main.tsx` calls `bootstrapSessionOutcome()` exactly ONCE, at
 * boot. If it resolves `'unreachable'`, `_authenticated.tsx`'s beforeLoad
 * enters the offline-shell branch and the tree renders with an EMPTY
 * in-memory auth store. When connectivity later returns, the drainer's own
 * `'online'` trigger (`use-foreground-drainer-triggers.ts`) fires a real
 * request with no bearer token, gets a 401, and (before the companion fix in
 * `packages/api-client/src/use-auth.ts`) `setupAuthClient`'s refresh
 * callback bailed out immediately — never tried the httpOnly refresh
 * cookie. Even with that fix, NOTHING was re-running `bootstrapSessionOutcome()`
 * itself from inside the offline-shell, so the route stayed on the cached
 * offline profile — and the SW-hostile pattern of relying on the item-level
 * 401-retry alone (with no route-level recovery) leaves the operator stuck
 * looking at the offline-shell UI even after the queue starts draining.
 *
 * This hook re-runs the SAME cookie-based bootstrap `main.tsx` uses at boot,
 * but from inside the already-mounted offline-shell, on two signals:
 *   - `online` — connectivity returned while the tab was open.
 *   - `visibilitychange` (become visible) — the operator backgrounded the
 *     PWA while offline and switches back after connectivity returned
 *     while it was hidden (an `online` event fired while backgrounded may
 *     be throttled/missed on some mobile browsers).
 *
 * On a successful recovery it: records the new boot outcome (so any FUTURE
 * `beforeLoad` re-evaluation also sees it), invalidates the router so
 * `_authenticated.tsx`'s `beforeLoad` re-runs immediately — dropping
 * `offlineProfile` and handing the tree a live session — and ticks the
 * drainer directly with the recovered identity so recovery does not depend
 * on that re-render + `useForegroundDrainerTriggers`'s own `hasSession`
 * dependency (see that hook) happening to run first.
 *
 * Single-flight via `inFlight`: guards against `online` and
 * `visibilitychange` firing back-to-back, or either firing again before a
 * prior attempt's side effects (`setAuthBootOutcome` + `router.invalidate` +
 * `tick`) have settled. The underlying `bootstrapSessionOutcome()` call is
 * ALSO de-duplicated (`packages/api-client`'s own shared refresh in-flight
 * promise), but that guard only spans the network call itself.
 *
 * MEDIUM fix (adversarial review) — only a `'session'` or `'unreachable'`
 * outcome is recorded via `setAuthBootOutcome`. A `'unreachable'` outcome
 * keeps the offline-shell branch open (correct: still no signal). But a
 * captive-portal network (an airport/hotel Wi-Fi splash page that answers
 * every request with a 200 HTML page or a 403, never reaching the real
 * backend) classifies as `'unauthenticated'`
 * (`classifyBootstrapFailure`'s conservative default for "any other
 * response") — NOT a genuine 401/403 from OUR OWN backend. Recording that
 * as the new boot outcome used to flip `getAuthBootOutcomeKind()` to
 * `'unauthenticated'`, and the NEXT navigation's `beforeLoad` (see
 * `_authenticated.tsx`) redirects straight to `/login` — where the operator
 * can never sign in either, since the captive portal still intercepts that
 * request too. Leaving the boot-time outcome untouched here keeps the
 * offline-shell open (still showing cached data, still lets the operator
 * capture check-ins offline) until a signal genuinely resolves the session
 * one way or the other.
 */
export function useOfflineShellRecovery(isOfflineShell: boolean): void {
  const router = useRouter();
  const inFlight = useRef(false);

  useEffect(() => {
    if (!isOfflineShell) return;

    async function attemptRecovery(): Promise<void> {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const outcome = await bootstrapSessionOutcome();
        if (outcome.kind !== 'unauthenticated') {
          setAuthBootOutcome(outcome);
        }
        if (outcome.kind !== 'session') return;
        await router.invalidate();
        const identity = resolveIdentity(outcome.session, null);
        if (identity) void foregroundDrainer.tick(identity);
      } finally {
        inFlight.current = false;
      }
    }

    const onOnline = () => void attemptRecovery();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void attemptRecovery();
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isOfflineShell, router]);
}
